import alt from "../alt";
import values from "lodash/values";
import indexBy from "lodash/keyBy";
import sortBy from "lodash/sortBy";
import EventBucketUtil from "../utils/EventBucketUtil";
import EventBucketActions from "../actions/EventBucketActions";
import UserStore from "../stores/UserStore";
import GlobalNotificationActions from "../actions/GlobalNotificationActions";

class EventBucketStore {
  constructor() {
    this.bindActions(EventBucketActions);

    this.state = {
      isFetchingAll: true,
      eventBuckets: {},
      configSchema: {},
      subdomainTestResults: {},
      newEventBucketId: null,
      isWorking: false
    };

    this.exportPublicMethods({
      getEventBuckets: this.getEventBuckets.bind(this),
      getEventBucketById: this.getEventBucketById.bind(this),
      getSubdomainTestResultsByEventBucketId: this.getSubdomainTestResultsByEventBucketId.bind(
        this
      ),
      getConfigSchema: this.getConfigSchema.bind(this)
    });
  }

  getEventBuckets() {
    return sortBy(values(this.state.eventBuckets), "created_at").reverse();
  }

  getEventBucketById(id) {
    return this.state.eventBuckets[id];
  }

  getSubdomainTestResultsByEventBucketId(id) {
    return this.state.subdomainTestResults[id];
  }

  getConfigSchema(id) {
    let schema = Object.assign({}, this.state.configSchema),
      bucket = this.getEventBucketById(id);

    // Hide sections if not applicable
    if (schema.sections && bucket) {
      if (!bucket.credits_enabled) {
        schema.sections = schema.sections.filter(s => s.key !== "credits");
      }
      if (!bucket.enable_virtual_festival) {
        schema.sections = schema.sections.filter(s => s.key !== "virtual");
      }
      // TEMP until we allow UI enabling of gift cards
      if (bucket.frontend_config && !bucket.frontend_config.gift_cards_enabled) {
        schema.sections = schema.sections.filter(s => s.key !== "gift_cards");
      }

      if (bucket.frontend_config && !bucket.frontend_config.memberships_enabled) {
        schema.sections = schema.sections.filter(s => s.key !== "memberships");
      }
    }

    return schema;
  }

  onFetchAll() {
    this.setState({ isFetchingAll: true });
    EventBucketUtil.fetchAll(UserStore.getState().apiKey)
      .then(data => {
        this.setState({ isFetchingAll: false });
        EventBucketActions.updateEventBuckets(indexBy(data, "id"));
      })
      .catch(EventBucketActions.fetchFailed);
  }

  onFetchOne(id) {
    this.setState({ isFetching: true });
    EventBucketUtil.fetchOne(UserStore.getState().apiKey, id)
      .then(data => {
        EventBucketActions.updateEventBucket(id, data);
        this.setState({ isFetching: false });
      })
      .catch(EventBucketActions.fetchFailed);
  }

  onEdit(newData) {
    EventBucketUtil.edit(
      UserStore.getState().apiKey,
      newData.id,
      newData.values
    )
      .then(data => {
        EventBucketActions.updateEventBucket(newData.id, data);
      })
      .catch(EventBucketActions.editFailed);
  }

  onUploadFile({ id, key, file, callback }) {
    EventBucketUtil.uploadFile(UserStore.getState().apiKey, id, key, file)
      .then(data => {
        let bucket = Object.assign({}, this.state.eventBuckets[id]);
        if (callback) {
          callback(data);
          return;
        }

        if (!bucket.UPLOADED_FILES) {
          bucket.UPLOADED_FILES = {};
        }
        bucket.UPLOADED_FILES[key] = data.url;
        EventBucketActions.updateEventBucket(id, bucket);
      })
      .catch(EventBucketActions.editFailed);
  }

  onTestSubdomain({ id, subdomain }) {
    EventBucketUtil.testSubdomain(UserStore.getState().apiKey, id, subdomain)
      .then(() => {
        let results = Object.assign(this.state.subdomainTestResults, {
          [id]: { [subdomain]: true }
        });
        this.setState({ subdomainTestResults: results });
      })
      .catch(err => {
        let results = Object.assign(this.state.subdomainTestResults, {
          [id]: { [subdomain]: err.message }
        });
        this.setState({ subdomainTestResults: results });
      });
  }

  onDuplicate({ id, fields }) {
    this.setState({ isWorking: true, newEventBucketId: null });

    EventBucketUtil.duplicate(UserStore.getState().apiKey, { id, fields })
      .then(data => {
        EventBucketActions.updateEventBucket(data.id, data);
        this.setState({ isWorking: false, newEventBucketId: data.id });
      })
      .catch(EventBucketActions.editFailed);
  }

  onDelete(id) {
    EventBucketUtil.delete(UserStore.getState().apiKey, id)
      .then(data => {
        EventBucketActions.updateEventBucket(id, undefined);
      })
      .catch(EventBucketActions.editFailed);
  }

  onCreate(newData) {
    this.setState({
      newEventBucketId: null
    });

    EventBucketUtil.create(UserStore.getState().apiKey, newData)
      .then(data => {
        EventBucketActions.updateEventBucket(data.id, data);
        this.setState({
          newEventBucketId: data.id
        });
      })
      .catch(EventBucketActions.editFailed);
  }

  onUpdateEventBuckets(eventBuckets) {
    this.setState({ eventBuckets });
  }

  onUpdateEventBucket(data) {
    if (!data.eventBucket) {
      delete this.state.eventBuckets[data.id];
    } else {
      this.state.eventBuckets[data.id] = data.eventBucket;
    }
    this.state.newEventBucketId = null;
    this.setState(this.state);
  }

  onFetchConfigSchema() {
    EventBucketUtil.fetchConfigSchema(UserStore.getState().apiKey)
      .then(configSchema => {
        this.setState({ configSchema });
      })
      .catch(EventBucketActions.fetchFailed);
  }

  onFetchFailed(e) {
    this.setState({ isFetchingAll: false, isFetching: false });
    console.error("Fetching event bucket failed:", e);
    GlobalNotificationActions.notify.defer(
      `Fetching event bucket failed: ${e.message}`
    );
    throw e;
  }

  onEditFailed(e) {
    alert(`Edit failed: ${e.message}`);

    // trigger event update to allow resubmit
    if (e._eventBucket) {
      this.state.eventBuckets[e._eventBucket].updated_at = Date.now();
    }
    this.setState({ eventBuckets: this.state.eventBuckets, isWorking: false });
  }
}

export default alt.createStore(EventBucketStore, "EventBucketStore");



// WEBPACK FOOTER //
// ./src/app/stores/EventBucketStore.js